import {useEffect, useRef, useState, type KeyboardEvent} from 'react';
import {ArrowUp, CircleAlert, CircleHelp, X} from 'lucide-react';
import {useIntl} from 'react-intl';
import {SkeletonBlock} from 'ch3-ui-lib';
import type {CallQuestion} from '../../../../store/features/calls/callsService';
import {insightsMessages as m} from '../insightsMessages';
import {bodyText, focusRing, iconButton, lucide} from '../insightsUi';
import {assistantErrorMessage, canRetry, type RateLimitMessages} from '../insightsErrors';
import {Citations} from '../summary/CitationChip';
import {QUESTION_MAX_LENGTH, useCallQuestions, type PendingQuestion} from './useCallQuestions';

const COUNTER_FROM = 400;

const askRateLimit: RateLimitMessages = {withMinutes: m.askRateLimited, soon: m.askRateLimitedSoon};

const QuestionBubble = ({text}: {text: string}) => (
    <div className="flex justify-end">
        <p className="m-0 max-w-[85%] whitespace-pre-wrap break-words rounded-card rounded-br-tail bg-brand-tint px-3.5 py-2 text-callout font-normal leading-[1.55] text-ink">
            {text}
        </p>
    </div>
);

const Answer = ({item}: {item: CallQuestion}) => {
    const intl = useIntl();
    return (
        <div className="flex flex-col gap-1">
            {!item.answerable && (
                <span className="inline-flex items-center gap-1 text-footnote font-semibold text-ink-muted">
                    <CircleHelp size={13} aria-hidden="true" className={lucide}/>
                    {intl.formatMessage(m.askNotCovered)}
                </span>
            )}
            <p className={`${bodyText} whitespace-pre-wrap break-words ${item.answerable ? 'text-ink' : 'text-ink-secondary'}`}>
                {item.answer}
                <Citations citations={item.citations}/>
            </p>
        </div>
    );
};

const Thinking = () => {
    const intl = useIntl();
    return (
        <div role="status" aria-label={intl.formatMessage(m.askThinking)} className="flex flex-col gap-1.5">
            <SkeletonBlock className="h-3 w-3/4"/>
            <SkeletonBlock className="h-3 w-1/2"/>
        </div>
    );
};

const PendingItem = ({pending, onRetry, onDismiss}: {pending: PendingQuestion; onRetry: () => void; onDismiss: () => void}) => {
    const intl = useIntl();
    const retryable = canRetry(pending.error);
    return (
        <li className="flex flex-col gap-2" data-testid="insights-ask-pending">
            <QuestionBubble text={pending.question}/>
            {pending.error ? (
                <div role="alert" className="flex flex-wrap items-center gap-2 text-footnote text-danger-ink">
                    <CircleAlert size={14} aria-hidden="true" className={lucide}/>
                    {assistantErrorMessage(intl, pending.error, m.askNeedsTranscript, m.askFailed, askRateLimit)}
                    {retryable && (
                        <button type="button" onClick={onRetry} className={`cursor-pointer border-0 bg-transparent p-0 font-semibold text-brand-ink underline ${focusRing}`}>
                            {intl.formatMessage(m.tryAgain)}
                        </button>
                    )}
                    <button type="button" aria-label={intl.formatMessage(m.close)} onClick={onDismiss} className={`${iconButton} size-6`}>
                        <X size={14} aria-hidden="true" className={lucide}/>
                    </button>
                </div>
            ) : (
                <Thinking/>
            )}
        </li>
    );
};

interface AskAiViewProps {
    callId: number;
    enabled: boolean;
}

export const AskAiView = ({callId, enabled}: AskAiViewProps) => {
    const intl = useIntl();
    const {history, loading, failed, refetch, pending, asking, ask, dismiss} = useCallQuestions(callId, enabled);
    const [text, setText] = useState('');
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const list = listRef.current;
        if (list) list.scrollTop = list.scrollHeight;
    }, [history.length, pending]);

    if (!enabled) {
        return <p className="m-0 px-6 py-4 text-callout text-ink-muted">{intl.formatMessage(m.askNeedsTranscript)}</p>;
    }

    const send = async (value: string) => {
        if (!value.trim() || asking) return;
        setText('');
        await ask(value);
    };

    const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
        event.preventDefault();
        void send(text);
    };

    const suggestions = [m.askSuggestion1, m.askSuggestion2, m.askSuggestion3, m.askSuggestion4].map((message) => intl.formatMessage(message));
    const showSuggestions = !loading && history.length === 0 && !pending;

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-2" data-testid="insights-ask-scroll">
                <p className="m-0 mb-4 text-footnote text-ink-muted">{intl.formatMessage(m.askIntro)}</p>
                {loading && (
                    <div className="flex flex-col gap-3" role="status" aria-label={intl.formatMessage(m.loading)}>
                        <SkeletonBlock className="ml-auto h-8 w-1/2" shape="card"/>
                        <SkeletonBlock className="h-3 w-3/4"/>
                    </div>
                )}
                {failed && (
                    <p role="alert" className="m-0 mb-4 inline-flex flex-wrap items-center gap-2 text-footnote text-danger-ink">
                        {intl.formatMessage(m.askHistoryFailed)}
                        <button type="button" onClick={() => void refetch()} className={`cursor-pointer border-0 bg-transparent p-0 font-semibold text-brand-ink underline ${focusRing}`}>
                            {intl.formatMessage(m.tryAgain)}
                        </button>
                    </p>
                )}
                <ol className="m-0 flex list-none flex-col gap-5 p-0" aria-live="polite">
                    {history.map((item) => (
                        <li key={item.id} className="flex flex-col gap-2" data-testid="insights-ask-item">
                            <QuestionBubble text={item.question}/>
                            <Answer item={item}/>
                        </li>
                    ))}
                    {pending && <PendingItem pending={pending} onRetry={() => void send(pending.question)} onDismiss={dismiss}/>}
                </ol>
                {showSuggestions && (
                    <div className="flex flex-col gap-2" aria-label={intl.formatMessage(m.askSuggestionsLabel)} role="group">
                        {suggestions.map((suggestion) => (
                            <button
                                key={suggestion}
                                type="button"
                                onClick={() => void send(suggestion)}
                                className={`w-fit cursor-pointer rounded-control border border-solid border-line bg-surface px-3 py-2 text-left text-callout text-ink hover:border-brand-disabled hover:bg-brand-tint-subtle ${focusRing}`}
                            >
                                {suggestion}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    void send(text);
                }}
                className="shrink-0 border-0 border-t border-solid border-line px-6 pb-16 pt-3"
            >
                <div className="flex items-end gap-2 rounded-card border border-solid border-line bg-surface px-3 py-2 focus-within:border-brand-focus focus-within:ring-2 focus-within:ring-brand-focus">
                    <textarea
                        value={text}
                        rows={1}
                        maxLength={QUESTION_MAX_LENGTH}
                        onChange={(event) => setText(event.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder={intl.formatMessage(m.askPlaceholder)}
                        aria-label={intl.formatMessage(m.askLabel)}
                        aria-describedby="insights-ask-hint"
                        className="max-h-32 min-h-[24px] flex-1 resize-none border-0 bg-transparent p-0 py-1 text-callout font-normal leading-[1.55] text-ink outline-none placeholder:text-ink-muted focus:ring-0 [field-sizing:content]"
                    />
                    <button
                        type="submit"
                        disabled={!text.trim() || asking}
                        aria-label={intl.formatMessage(m.askSend)}
                        className={`inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-control border-0 bg-brand p-0 text-white hover:bg-brand-hover disabled:cursor-default disabled:bg-brand-disabled ${focusRing}`}
                    >
                        <ArrowUp size={16} aria-hidden="true" className={lucide}/>
                    </button>
                </div>
                <div className="mt-1.5 flex items-center justify-between gap-2 text-footnote text-ink-muted">
                    <span id="insights-ask-hint">{intl.formatMessage(m.askEnterHint)}</span>
                    {text.length >= COUNTER_FROM && (
                        <span className={`tabular-nums ${text.length >= QUESTION_MAX_LENGTH ? 'text-danger-ink' : ''}`}>
                            {intl.formatMessage(m.askCounter, {length: text.length, limit: QUESTION_MAX_LENGTH})}
                        </span>
                    )}
                </div>
            </form>
        </div>
    );
};
